import {
  Typography,
  Card,
  CardContent,
  Box,
  Link,
  Divider,
} from "@mui/material";
import * as React from "react";
import useResponsiveHelper from "../hooks/useResponsiveHelper";

export default function LocationCard(props) {
  const { isMobile } = useResponsiveHelper();

  const {
    name,
    hours = [],
    number,
    location,
    map,
    storeImg,
  } = props.location ?? {};

  const mapHref = "https://www.google.com/maps?q=";

  const imgSize = isMobile ? 110 : 170;

  const safeTel = number ? `tel:${String(number).replace(/\s+/g, "")}` : undefined;
  const safeMapUrl = map ? `${mapHref}${map}` : undefined;

  // split hours into two columns on desktop so it fills space better
  const leftHours = isMobile ? hours : hours.filter((_, i) => i % 2 === 0);
  const rightHours = isMobile ? [] : hours.filter((_, i) => i % 2 === 1);

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: 2,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
        {/* Top row */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
          {/* Image */}
          <Box
            sx={{
              width: imgSize,
              height: imgSize,
              borderRadius: 2,
              overflow: "hidden",
              flexShrink: 0,
              bgcolor: "rgba(0,0,0,0.05)",
            }}
          >
            <Box
              component="img"
              alt="storefront"
              src={storeImg}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>

          {/* Info */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: 800, lineHeight: 1.1 }}
                noWrap
                title={name}
              >
                {name}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.75 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Address:{" "}
                </Box>
                {safeMapUrl ? (
                  <Link
                    href={safeMapUrl}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    color="secondary"
                  >
                    {location}
                  </Link>
                ) : (
                  <Box component="span">{location ?? "N/A"}</Box>
                )}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Phone:{" "}
                </Box>
                {safeTel ? (
                  <Link href={safeTel} underline="hover" color="secondary">
                    {number}
                  </Link>
                ) : (
                  <Box component="span">N/A</Box>
                )}
              </Typography>
            </Box>

            {/* Small footer line to balance layout */}
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Tap address to open Google Maps
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: "rgba(0,0,0,0.15)" }} />

        {/* Hours panel (fills space visually) */}
        <Box
          sx={{
            borderRadius: 2,
            p: 1.5,
            bgcolor: "rgba(0,0,0,0.03)", // makes it feel “filled” even with few hours
          }}
        >
          <Typography variant={isMobile ? "body1" : "body1"} sx={{ fontWeight: 800, mb: 1 }}>
            Store Hours
          </Typography>

          {hours.length ? (
            isMobile ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {hours.map((time, idx) => (
                  <Typography key={`${name}-hour-${idx}`} variant="body2" sx={{ opacity: 0.9 }}>
                    {time}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {leftHours.map((time, idx) => (
                    <Typography key={`${name}-hour-left-${idx}`} variant="body2" sx={{ opacity: 0.9 }}>
                      {time}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {rightHours.map((time, idx) => (
                    <Typography key={`${name}-hour-right-${idx}`} variant="body2" sx={{ opacity: 0.9 }}>
                      {time}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Hours unavailable
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}